import {
  createRouter,
  createRootRouteWithContext,
  type AnyRouter,
  type AnyRoute,
} from '@tanstack/react-router';
import { ApolloClient } from '@apollo/client';
import ReactGA from 'react-ga4';
import { useGlobalUIStore } from '../stores/global-ui-store';
import type { AuthState } from '../contexts/auth-context';

/**
 * Router context interface — provides Apollo Client and Auth state
 * to all route loaders via the router context injection pattern.
 *
 * Components that need these can access them from route context instead
 * of using module-level getApolloClient() singletons.
 */
export interface AmberRouterContext {
  apolloClient: ApolloClient;
  auth?: AuthState;
}

export { createRootRouteWithContext };

let _router: AnyRouter | null = null;

/**
 * Creates the TanStack Router instance from a generated route tree and
 * subscribes to location changes for analytics and UI effects.
 *
 * Called once during app initialization. The returned router is passed
 * to <RouterProvider> and also used by the history shim for navigation.
 *
 * Each app package passes its own generated routeTree (from routeTree.gen.ts)
 * since the route tree is generated per-app by @tanstack/router-plugin/vite.
 */
export function initRouter(routeTree: AnyRoute): AnyRouter {
  _router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    // Scroll restoration — saves/restores scroll position on back/forward navigation.
    // Without this, navigating back to a long-scrolled page (Operations View, settings)
    // resets to the top. TanStack Router handles this for all scrollable containers.
    scrollRestoration: true,
    // Show a pending component after 200ms if a loader is still running.
    // The default 1000ms feels unresponsive during bin navigation. Routes can
    // override with their own pendingMs.
    defaultPendingMs: 200,
    context: {
      // These are set to undefined here because they're provided at render time
      // via <RouterProvider context={...} />. The router requires initial values
      // but the real instances are injected when the app renders.
      apolloClient: undefined as unknown as ApolloClient,
    },
  });

  // Analytics + nav bar side effects on navigation.
  // Previously handled by:
  // - (Previously) ui-analytics-watcher saga — now handled directly below
  // - nav-bar reducer (case LOCATION_CHANGE → close sidebar)
  // Moved here because LOCATION_CHANGE was removed — the URL is now the source
  // of truth for container selection, so Redux no longer tracks router state.
  // This subscribe callback is the single point where completed navigations
  // are observed, replacing both mechanisms with a direct implementation.
  _router.subscribe('onResolved', ({ toLocation }) => {
    // Close nav bar on navigation (replaces old changeLocation case in nav-bar reducer)
    useGlobalUIStore.getState().setNavBarOpen(false);

    ReactGA.event({
      category: 'nav',
      action: 'PUSH',
    });
    ReactGA.set({ page: toLocation.pathname });
    ReactGA.send({ hitType: 'pageview', page: toLocation.pathname });
  });

  return _router;
}

/**
 * Returns the module-level router instance, or null if initRouter()
 * has not been called yet. The history shim checks for null and falls
 * back to window.location before the router is ready.
 */
export function getRouter(): AnyRouter | null {
  return _router;
}
